import type { HubConnection } from "@microsoft/signalr";
import { createSignalRConnection } from "./connection";
import { AppThunk } from "../store/action-creators";
import { AppState } from "../store";
import {
  handleConversationAssigned,
  handleConversationTransferred,
  handleConversationUnlocked,
  handleConversationLocked,
  handleConversationClosed,
  handleConversationReopened,
  handleConversationUpdated,
  handleOperationFailed,
} from "./handlers";
import {
  addNotifications,
  signalrConnecting,
  signalrConnected,
  signalrDisconnected,
  signalrError,
  signalrEventReceived,
} from "../store/action-creators";
import type {
  ConversationAssignedEvent,
  ConversationTransferredEvent,
  ConversationUnlockedEvent,
  ConversationClosedEvent,
  ConversationReopenedEvent,
  ConversationUpdatedEvent,
  SignalREvent,
  OperationFailedEvent,
  ConversationLockedEvent,
} from "./types";

let connection: HubConnection | null = null;
let started = false;

export const startSignalR =
  (accessToken: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    if (started) return;

    if (!connection) {
      connection = createSignalRConnection(accessToken);
    }

    const conn = connection;
    dispatch(signalrConnecting());

    const wrapEvent = (evt: SignalREvent) => {
      dispatch(signalrEventReceived(evt));
    };

    // ConversationAssigned
    conn.on("ConversationAssigned", (raw) => {
      const evt = raw as ConversationAssignedEvent;
      wrapEvent(evt);
      handleConversationAssigned(
        evt,
        dispatch as any,
        getState as () => AppState
      );
    });

    // ConversationTransferred
    conn.on("ConversationTransferred", (raw) => {
      const evt = raw as ConversationTransferredEvent;
      wrapEvent(evt);
      handleConversationTransferred(
        evt,
        dispatch as any,
        getState as () => AppState
      );
    });

    // ConversationUnlocked
    conn.on("ConversationUnlocked", (raw) => {
      const evt = raw as ConversationUnlockedEvent;
      wrapEvent(evt);
      handleConversationUnlocked(
        evt,
        dispatch as any,
        getState as () => AppState
      );
    });

    // ConversationLocked
    conn.on("ConversationLocked", (raw) => {
      const evt = raw as ConversationLockedEvent;
      wrapEvent(evt);
      handleConversationLocked(
        evt,
        dispatch as any,
        getState as () => AppState
      );
    });

    // ConversationClosed
    conn.on("ConversationClosed", (raw) => {
      const evt = raw as ConversationClosedEvent;
      wrapEvent(evt);
      handleConversationClosed(
        evt,
        dispatch as any,
        getState as () => AppState
      );
    });

    // ConversationReopened
    conn.on("ConversationReopened", (raw) => {
      const evt = raw as ConversationReopenedEvent;
      wrapEvent(evt);
      handleConversationReopened(
        evt,
        dispatch as any,
        getState as () => AppState
      );
    });

    // ConversationUpdated
    conn.on("ConversationUpdated", (raw) => {
      const evt = raw as ConversationUpdatedEvent;
      wrapEvent(evt);
      handleConversationUpdated(
        evt,
        dispatch as any,
        getState as () => AppState
      );
    });

    // OperationFailed
    conn.on("OperationFailed", (raw) => {
      const evt = {
        type: "OperationFailed",
        ...(raw as any),
      } as OperationFailedEvent;
      wrapEvent(evt);
      handleOperationFailed(evt, dispatch as any, getState as () => AppState);
    });

    conn.onreconnected(() => {
      dispatch(signalrConnected());
      dispatch(
        addNotifications([
          {
            id: Date.now(),
            message: "Connessione notifiche ristabilita.",
            variant: "success",
            dismissAfter: 2000,
          },
        ])
      );
    });

    conn.onclose((err) => {
      started = false;
      dispatch(signalrDisconnected());
      if (err) {
        dispatch(signalrError(err.message));
      }
      dispatch(
        addNotifications([
          {
            id: Date.now(),
            message: "Connessione notifiche interrotta.",
            variant: "error",
            dismissAfter: 2000,
          },
        ])
      );
    });

    try {
      await conn.start();
      started = true;
      dispatch(signalrConnected());
    } catch (error: any) {
      started = false;
      dispatch(signalrDisconnected());
      dispatch(signalrError(error?.message ?? "Connessione SignalR fallita"));
      dispatch(
        addNotifications([
          {
            id: Date.now(),
            message: "Errore di connessione alle notifiche.",
            variant: "error",
            dismissAfter: 3000,
          },
        ])
      );
      throw error;
    }
  };

export const stopSignalR = (): AppThunk<Promise<void>> => {
  return async (dispatch) => {
    if (connection && started) {
      await connection.stop();
    }
    started = false;
    dispatch(signalrDisconnected());
  };
};
